import React, { Component } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Prompt } from 'react-router-dom';
import { getAPI, postAPI, putAPI } from '../../Base/API';
import { handleNotification } from '../../Base/Common/Notification';
import { canUseAi } from '../../Base/Authorization';
import { StyledCard } from '../../Base/Common/CommonUIComponents';
import ConfirmActionModal from '../../Base/Common/ConfirmActionModal';
import DesignerToolbar from './DesignerToolbar';
import ElementPalette from './ElementPalette';
import DesignerCanvas from './DesignerCanvas';
import PropertyInspector from './PropertyInspector';
import TemplateDesignerPreviewModal from './TemplateDesignerPreviewModal';
import AiAssistantPanel from './AiAssistantPanel';
import NewTemplateChoiceModal from './NewTemplateChoiceModal';
import {
    createDefaultElement,
    getPageDimsMm,
    TEMPLATE_LANGUAGES,
    getInitialPreviewLang,
    getOrderedTemplateLanguages,
    normalizeFreeText,
    getPreviewText,
    buildPrintHtml,
    SAMPLE_BINDINGS,
    generateId,
    clampElement,
    fitElementToPage,
    createStarterElements,
} from './templateUtils';

const DEFAULT_ZOOM = 0.75;

// Internal model instruction (NOT user-facing) for the "Start with base layout (AI)"
// action on a new, blank template. Asks the assistant to lay out a clean gift-voucher
// template for the current page, with each element bound to its catalog token. The
// endpoint + system prompt already constrain output to the element schema, mm page
// bounds, and full {{token}} bindings — this just describes the desired base layout.
const BASE_LAYOUT_SEED =
    'Create a clean, professional gift-voucher base layout for the current empty page. ' +
    'Add ALL of these elements (they are the standard voucher set — none may be omitted), ' +
    'each bound to its token, laid out cleanly within the page bounds with readable styling:\n' +
    '- An image for the hotel logo bound to {{VoucherTemplateConfig.LogoImage}} near the top.\n' +
    '- A prominent heading bound to {{VoucherType.Title}} below the logo.\n' +
    '- A description text bound to {{VoucherType.Description}} under the heading.\n' +
    '- A prominent amount bound to {{Amount}}.\n' +
    '- The valid-to date bound to {{ValidTo}}.\n' +
    '- A QR element bound to {{VoucherCode}} in a bottom corner, with the voucher code as small\n' +
    '  text bound to {{VoucherCode}} right below it.\n' +
    '- The terms & conditions in small print near the bottom, bound to {{VoucherType.Tec.Description}}.\n' +
    'LAYOUT RULES: elements must NEVER overlap each other. Work in horizontal bands from top to\n' +
    'bottom: logo band, then title, then description, then amount + validity in the middle, and\n' +
    'reserve the bottom fifth of the page for the QR + code (one corner) and the conditions text\n' +
    '(remaining width, small font). Leave clear vertical spacing between bands.';

class VoucherTemplateDesigner extends Component {
    static displayName = VoucherTemplateDesigner.name;

    _fileInputRef = React.createRef();

    _highlightTimeout = null;

    // Guards async API callbacks against setState-after-unmount (React warning / leak).
    _isMounted = false;

    constructor(props) {
        super(props);

        this.state = {
            block: false,
            error: null,

            // Template state
            template: {
                id: null,
                name: '',
                pageSize: 'A4',
                orientation: 'portrait',
                bgColour: '#ffffff',
                bgImage: '',
                bgImageOpacity: 100,
                pageMarginX: 0,
                pageMarginY: 0,
                elements: [],
            },

            // UI state
            selectedElementId: null,
            selectedElementIds: [],
            zoom: DEFAULT_ZOOM,
            previewLang: getInitialPreviewLang(),
            isDirty: false,

            // Modal state
            previewModal: false,
            unsavedChangesModal: false,
            pendingNavigation: null,

            // AI Assistant state
            aiPanelOpen: false,
            // aiTranscript entries: { role, text, changesSummary?, preOpSnapshot?, undone?, isError?, errorType? }
            aiTranscript: [],
            aiPending: false,
            aiHighlightedIds: new Set(),

            // New-template base-layout choice (shown once per session for a from-scratch template)
            newTemplateChoiceModal: false,
            baseLayoutPending: false,
        };
    }

    componentDidMount() {
        this._isMounted = true;
        window.addEventListener('beforeunload', this._handleBeforeUnload);
        const { id } = this.props.match.params;
        if (id) {
            this._loadTemplate(id);
        } else {
            // New template from scratch: offer an AI-generated base layout once.
            this.setState({ newTemplateChoiceModal: true });
            this._loadBaseTemplates();
        }
    }

    // Base designs (US 25001) offered in the new-template modal; if they fail to load the user is
    // warned and the modal keeps the blank and AI starts
    _loadBaseTemplates = () => {
        getAPI(result => {
            const { data, error } = result;
            if (!this._isMounted) return;

            if (error || !data || (data.errors && data.errors.length > 0)) {
                handleNotification(data && data.errors && data.errors.length > 0 ? data : {
                    warnings: [{ message: this.props.intl.formatMessage({ id: 'TemplateAssistant.NewTemplate.InspirationFailed' }) }],
                });
                return;
            }
            if (!data.response) return;

            const baseTemplates = data.response.map(d => {
                let layout = {};
                try {
                    const parsed = JSON.parse(d.layoutJson);
                    layout = Array.isArray(parsed) ? { elements: parsed } : (parsed || {});
                } catch (e) {
                    return null;
                }
                return { ...layout, id: d.id, name: d.name, pageSize: d.pageSize || 'A4', orientation: d.orientation || 'portrait', elements: layout.elements || [] };
            }).filter(Boolean);

            this.setState({ baseTemplates });
        }, '/api/Voucher/Config/v1/voucherTemplateDesign/base');
    };

    componentWillUnmount() {
        this._isMounted = false;
        window.removeEventListener('beforeunload', this._handleBeforeUnload);
        if (this._highlightTimeout) {
            clearTimeout(this._highlightTimeout);
            this._highlightTimeout = null;
        }
    }

    // ─── Navigation guard (browser-level) ─────────────────────────────────────

    _handleBeforeUnload = (e) => {
        if (this.state.isDirty) {
            e.preventDefault();
            e.returnValue = '';
        }
    };

    // ─── API ───────────────────────────────────────────────────────────────────

    _loadTemplate = (id) => {
        this.setState({ block: true });

        getAPI(result => {
            const { data, error } = result;

            if (error) {
                this.setState({
                    error: [{ message: error.message, messageType: 'danger' }],
                    block: false,
                });
                return;
            }

            if (data && data.response && data.response.length > 0) {
                const t = data.response[0];
                let elements = [];
                let bgColour = '#ffffff';
                let bgImage = '';
                let bgImageOpacity = 100;
                let pageMarginX = 0;
                let pageMarginY = 0;
                try {
                    const parsed = JSON.parse(t.layoutJson);
                    if (Array.isArray(parsed)) {
                        elements = parsed;
                    } else if (parsed && typeof parsed === 'object') {
                        elements = parsed.elements || [];
                        bgColour = parsed.bgColour || '#ffffff';
                        bgImage = parsed.bgImage || '';
                        bgImageOpacity = parsed.bgImageOpacity ?? 100;
                        pageMarginX = parsed.pageMarginX || 0;
                        pageMarginY = parsed.pageMarginY || 0;
                    }
                } catch (e) {
                    elements = [];
                }

                // Migrate legacy string freeText to multilang object
                const migratedElements = elements.map(el =>
                    el.freeText !== undefined ? { ...el, freeText: normalizeFreeText(el.freeText) } : el
                );

                this.setState({
                    block: false,
                    isDirty: false,
                    template: {
                        id: t.id,
                        name: t.name || '',
                        pageSize: t.pageSize || 'A4',
                        orientation: t.orientation || 'portrait',
                        bgColour,
                        bgImage,
                        bgImageOpacity,
                        pageMarginX,
                        pageMarginY,
                        elements: migratedElements,
                    },
                });
            } else {
                this.setState({ block: false });
            }
        }, `/api/Voucher/Config/v1/voucherTemplateDesign/${id}`);
    };

    _buildPayload = () => {
        const { template } = this.state;

        // Persistable HTML per language, with bindings kept as Handlebars tokens for the
        // server to resolve at send/print time. renderedHtml carries the base (default)
        // language; translations carries every language keyed by upper-case code.
        const translations = {};
        TEMPLATE_LANGUAGES.forEach(({ code }) => {
            translations[code.toUpperCase()] = buildPrintHtml(template, template.elements, {}, code, { emitTokens: true });
        });

        return {
            name: template.name,
            pageSize: template.pageSize,
            orientation: template.orientation,
            layoutJson: JSON.stringify({
                elements: template.elements,
                bgColour: template.bgColour,
                bgImage: template.bgImage,
                bgImageOpacity: template.bgImageOpacity ?? 100,
                pageMarginX: template.pageMarginX,
                pageMarginY: template.pageMarginY,
            }),
            active: true,
            renderedHtml: translations[getInitialPreviewLang().toUpperCase()],
            translations: JSON.stringify(translations),
        };
    };

    _validate = () => {
        const { template } = this.state;

        if (!template.name || !template.name.trim()) {
            handleNotification(
                { errors: [{ message: <FormattedMessage id="TemplateDesigner.SaveError.NoName" /> }] }
            );
            return false;
        }

        if (!template.elements || template.elements.length === 0) {
            handleNotification(
                { errors: [{ message: <FormattedMessage id="TemplateDesigner.SaveError.NoElements" /> }] }
            );
            return false;
        }

        return true;
    };

    _save = (callback) => {
        if (!this._validate()) return;

        this.setState({ block: true });

        const { template } = this.state;
        const payload = this._buildPayload();

        if (template.id) {
            // Update existing
            putAPI(result => {
                const { data, error } = result;
                if (error) {
                    this.setState({ error: [{ message: error.message, messageType: 'danger' }], block: false });
                    return;
                }
                if (data) {
                    if (data.errors && data.errors.length > 0) {
                        handleNotification(data);
                        this.setState({ block: false });
                    } else {
                        handleNotification(data, <FormattedMessage id="TemplateDetails.TemplateSaved" />, <FormattedMessage id="generic.success" />);
                        this.setState({ block: false, isDirty: false }, () => {
                            if (callback) callback();
                        });
                    }
                }
            }, `/api/Voucher/Config/v1/voucherTemplateDesign/${template.id}`, payload);
        } else {
            // Create new
            postAPI(result => {
                const { data, error } = result;
                if (error) {
                    this.setState({ error: [{ message: error.message, messageType: 'danger' }], block: false });
                    return;
                }
                if (data) {
                    if (data.errors && data.errors.length > 0) {
                        handleNotification(data);
                        this.setState({ block: false });
                    } else {
                        const savedId = data.response && data.response[0] && data.response[0].id;
                        handleNotification(data, <FormattedMessage id="TemplateDetails.TemplateSaved" />, <FormattedMessage id="generic.success" />);
                        this.setState(prevState => ({
                            block: false,
                            isDirty: false,
                            template: {
                                ...prevState.template,
                                id: savedId || prevState.template.id,
                            },
                        }), () => {
                            if (callback) callback();
                        });
                    }
                }
            }, '/api/Voucher/Config/v1/voucherTemplateDesign', payload);
        }
    };

    // ─── Toolbar handlers ──────────────────────────────────────────────────────

    _handleNameChange = (name) => {
        this.setState(prevState => ({
            template: { ...prevState.template, name },
            isDirty: true,
        }));
    };

    _handlePageSizeChange = (pageSize) => {
        this.setState(prevState => {
            // a format switch must never leave elements stranded off-page
            const dims = getPageDimsMm(pageSize, prevState.template.orientation);
            return {
                template: {
                    ...prevState.template,
                    pageSize,
                    elements: prevState.template.elements.map(el => fitElementToPage(el, dims.w, dims.h)),
                },
                isDirty: true,
            };
        });
    };

    _handleOrientationChange = (orientation) => {
        this.setState(prevState => {
            const dims = getPageDimsMm(prevState.template.pageSize, orientation);
            return {
                template: {
                    ...prevState.template,
                    orientation,
                    elements: prevState.template.elements.map(el => fitElementToPage(el, dims.w, dims.h)),
                },
                isDirty: true,
            };
        });
    };

    _handleBgColourChange = (bgColour) => {
        this.setState(prevState => ({
            template: { ...prevState.template, bgColour },
            isDirty: true,
        }));
    };

    _handleBgImageOpacityChange = (bgImageOpacity) => {
        this.setState(prevState => ({
            template: { ...prevState.template, bgImageOpacity: Math.max(0, Math.min(100, bgImageOpacity)) },
            isDirty: true,
        }));
    };

    _handleBgImageChange = (bgImage) => {
        this.setState(prevState => ({
            template: { ...prevState.template, bgImage },
            isDirty: true,
        }));
    };

    _handlePageMarginXChange = (pageMarginX) => {
        this.setState(prevState => ({
            template: { ...prevState.template, pageMarginX },
            isDirty: true,
        }));
    };

    _handlePageMarginYChange = (pageMarginY) => {
        this.setState(prevState => ({
            template: { ...prevState.template, pageMarginY },
            isDirty: true,
        }));
    };

    _handleSave = () => {
        this._save();
    };

    _handleSaveClose = () => {
        this._save(() => {
            this.props.history.push('/VoucherTemplates');
        });
    };

    _handlePreview = () => {
        this.setState({ previewModal: true });
    };

    _togglePreviewModal = () => {
        this.setState(prevState => ({ previewModal: !prevState.previewModal }));
    };

    // ─── Canvas / element handlers ─────────────────────────────────────────────

    _handleAddElementByType = (type) => {
        const { template } = this.state;
        const dims = getPageDimsMm(template.pageSize, template.orientation);
        const el = createDefaultElement(type, dims.w, dims.h);

        // Assign zIndex above all existing
        const maxZ = template.elements.reduce((acc, e) => Math.max(acc, e.zIndex || 1), 0);
        el.zIndex = maxZ + 1;

        this.setState(prevState => ({
            template: {
                ...prevState.template,
                elements: [...prevState.template.elements, el],
            },
            selectedElementId: el.id,
            selectedElementIds: [el.id],
            isDirty: true,
        }));
    };

    _handleAddElementObject = (el) => {
        const { template } = this.state;
        const maxZ = template.elements.reduce((acc, e) => Math.max(acc, e.zIndex || 1), 0);
        const newEl = { ...el, zIndex: maxZ + 1 };

        this.setState(prevState => ({
            template: {
                ...prevState.template,
                elements: [...prevState.template.elements, newEl],
            },
            selectedElementId: newEl.id,
            selectedElementIds: [newEl.id],
            isDirty: true,
        }));
    };

    // additive (Ctrl/Shift/Cmd) toggles the element in the multi-selection; the last
    // selected stays the "primary" shown in the inspector
    _handleSelectElement = (id, additive = false) => {
        this.setState(prevState => {
            if (id === null) {
                return { selectedElementId: null, selectedElementIds: [] };
            }

            const current = prevState.selectedElementIds || [];

            if (additive) {
                const has = current.includes(id);
                const next = has ? current.filter(x => x !== id) : [...current, id];
                return {
                    selectedElementIds: next,
                    selectedElementId: has ? (next[next.length - 1] || null) : id,
                };
            }

            return { selectedElementId: id, selectedElementIds: [id] };
        });
    };

    _handleMoveElement = (id, newX, newY) => {
        this.setState(prevState => {
            const source = prevState.template.elements.find(el => el.id === id);
            if (!source) return null;

            // dragging a multi-selection member moves the whole group by the same delta
            const sel = prevState.selectedElementIds || [];
            const groupIds = (sel.includes(id) && sel.length > 1) ? new Set(sel) : new Set([id]);

            const dx = newX - source.x;
            const dy = newY - source.y;
            const dims = getPageDimsMm(prevState.template.pageSize, prevState.template.orientation);

            return {
                template: {
                    ...prevState.template,
                    elements: prevState.template.elements.map(el => {
                        if (!groupIds.has(el.id) || el.locked) return el;
                        const nx = Math.max(0, Math.min(el.x + dx, dims.w - el.w));
                        const ny = Math.max(0, Math.min(el.y + dy, dims.h - el.h));
                        return { ...el, x: nx, y: ny };
                    }),
                },
                isDirty: true,
            };
        });
    };

    _handleDuplicateElement = (id) => {
        this.setState(prevState => {
            const source = prevState.template.elements.find(el => el.id === id);
            if (!source) return null;

            const dims = getPageDimsMm(prevState.template.pageSize, prevState.template.orientation);
            const copy = {
                ...source,
                id: generateId(),
                x: Math.max(0, Math.min(source.x + 5, dims.w - source.w)),
                y: Math.max(0, Math.min(source.y + 5, dims.h - source.h)),
            };

            return {
                template: { ...prevState.template, elements: [...prevState.template.elements, copy] },
                selectedElementId: copy.id,
                selectedElementIds: [copy.id],
                isDirty: true,
            };
        });
    };

    // target 'selection': each element aligns to the selection bbox; target 'page':
    // single element to the page, multi moves as a group (bbox aligned, spacing kept)
    _handleAlign = (mode, target = 'selection') => {
        this.setState(prevState => {
            const sel = prevState.selectedElementIds || [];
            const ids = sel.length ? sel : (prevState.selectedElementId ? [prevState.selectedElementId] : []);
            if (ids.length === 0) return null;

            const dims = getPageDimsMm(prevState.template.pageSize, prevState.template.orientation);
            const targets = prevState.template.elements.filter(el => ids.includes(el.id) && !el.locked);
            if (targets.length === 0) return null;

            const minX = Math.min(...targets.map(e => e.x));
            const minY = Math.min(...targets.map(e => e.y));
            const maxR = Math.max(...targets.map(e => e.x + e.w));
            const maxB = Math.max(...targets.map(e => e.y + e.h));
            const bbox = { x: minX, y: minY, w: maxR - minX, h: maxB - minY };

            const idSet = new Set(targets.map(t => t.id));
            const clampRound = (v, max) => Math.round(Math.max(0, Math.min(v, max)) * 10) / 10;

            let mapEl;

            if (target === 'page' && targets.length > 1) {
                let dx = 0, dy = 0;
                switch (mode) {
                    case 'left':    dx = -bbox.x; break;
                    case 'centerH': dx = (dims.w - bbox.w) / 2 - bbox.x; break;
                    case 'right':   dx = dims.w - bbox.w - bbox.x; break;
                    case 'top':     dy = -bbox.y; break;
                    case 'middleV': dy = (dims.h - bbox.h) / 2 - bbox.y; break;
                    case 'bottom':  dy = dims.h - bbox.h - bbox.y; break;
                    default: break;
                }
                mapEl = (el) => ({ ...el, x: clampRound(el.x + dx, dims.w - el.w), y: clampRound(el.y + dy, dims.h - el.h) });
            } else {
                const box = (target === 'page' || targets.length === 1)
                    ? { x: 0, y: 0, w: dims.w, h: dims.h }
                    : bbox;

                mapEl = (el) => {
                    let { x, y } = el;
                    switch (mode) {
                        case 'left':    x = box.x; break;
                        case 'centerH': x = box.x + (box.w - el.w) / 2; break;
                        case 'right':   x = box.x + box.w - el.w; break;
                        case 'top':     y = box.y; break;
                        case 'middleV': y = box.y + (box.h - el.h) / 2; break;
                        case 'bottom':  y = box.y + box.h - el.h; break;
                        default: break;
                    }
                    return { ...el, x: clampRound(x, dims.w - el.w), y: clampRound(y, dims.h - el.h) };
                };
            }

            return {
                template: {
                    ...prevState.template,
                    elements: prevState.template.elements.map(el => (idSet.has(el.id) ? mapEl(el) : el)),
                },
                isDirty: true,
            };
        });
    };

    _handleRadiusElement = (id, borderRadius) => {
        this.setState(prevState => ({
            template: {
                ...prevState.template,
                elements: prevState.template.elements.map(el =>
                    el.id === id ? { ...el, borderRadius } : el
                ),
            },
            isDirty: true,
        }));
    };

    _handleRotateElement = (id, rotation) => {
        this.setState(prevState => ({
            template: {
                ...prevState.template,
                elements: prevState.template.elements.map(el =>
                    el.id === id ? { ...el, rotation } : el
                ),
            },
            isDirty: true,
        }));
    };

    _handleResizeElement = (id, newX, newY, newW, newH) => {
        this.setState(prevState => ({
            template: {
                ...prevState.template,
                elements: prevState.template.elements.map(el =>
                    el.id === id ? { ...el, x: newX, y: newY, w: newW, h: newH } : el
                ),
            },
            isDirty: true,
        }));
    };

    _handleChangeElement = (updatedEl) => {
        this.setState(prevState => ({
            template: {
                ...prevState.template,
                elements: prevState.template.elements.map(el =>
                    el.id === updatedEl.id ? updatedEl : el
                ),
            },
            isDirty: true,
        }));
    };

    // "Show" condition on every selected element (empty = always shown, the property is dropped)
    _handleVisibilityChange = (visibleWhen) => {
        this.setState(prevState => {
            const ids = prevState.selectedElementIds && prevState.selectedElementIds.length
                ? prevState.selectedElementIds
                : [prevState.selectedElementId];
            return {
                template: {
                    ...prevState.template,
                    elements: prevState.template.elements.map(el => {
                        if (!ids.includes(el.id)) return el;
                        const next = { ...el, visibleWhen };
                        if (!visibleWhen) delete next.visibleWhen;
                        return next;
                    }),
                },
                isDirty: true,
            };
        });
    };

    _handleDeleteElement = (id) => {
        this.setState(prevState => {
            const target = prevState.template.elements.find(el => el.id === id);
            if (target && target.locked) return null;

            return {
                template: {
                    ...prevState.template,
                    elements: prevState.template.elements.filter(el => el.id !== id),
                },
                selectedElementId: prevState.selectedElementId === id ? null : prevState.selectedElementId,
                selectedElementIds: (prevState.selectedElementIds || []).filter(x => x !== id),
                isDirty: true,
            };
        });
    };

    // Move one step in the z-order; zIndexes may be duplicated, so the stack is first
    // normalised to sequential values from the current visual order
    _handleMoveLayer = (id, direction) => {
        this.setState(prevState => {
            const ordered = [...prevState.template.elements].sort((a, b) => (a.zIndex || 1) - (b.zIndex || 1));
            const idx = ordered.findIndex(el => el.id === id);
            if (idx === -1) return null;

            const target = idx + direction;
            if (target < 0 || target >= ordered.length) return null;

            [ordered[idx], ordered[target]] = [ordered[target], ordered[idx]];

            const newZById = new Map(ordered.map((el, i) => [el.id, i + 1]));

            return {
                template: {
                    ...prevState.template,
                    elements: prevState.template.elements.map(el => ({ ...el, zIndex: newZById.get(el.id) })),
                },
                isDirty: true,
            };
        });
    };

    // Drag-and-drop reorder: dragged element takes the target's position in the stack
    _handleReorderLayer = (draggedId, targetId) => {
        if (draggedId === targetId) return;

        this.setState(prevState => {
            const orderedDesc = [...prevState.template.elements].sort((a, b) => (b.zIndex || 1) - (a.zIndex || 1));
            const from = orderedDesc.findIndex(el => el.id === draggedId);
            const to = orderedDesc.findIndex(el => el.id === targetId);
            if (from === -1 || to === -1) return null;

            const [moved] = orderedDesc.splice(from, 1);
            orderedDesc.splice(to, 0, moved);
            const n = orderedDesc.length;
            const newZById = new Map(orderedDesc.map((el, i) => [el.id, n - i]));

            return {
                template: {
                    ...prevState.template,
                    elements: prevState.template.elements.map(el => ({ ...el, zIndex: newZById.get(el.id) })),
                },
                isDirty: true,
            };
        });
    };

    _handleToggleLockElement = (id) => {
        this.setState(prevState => ({
            template: {
                ...prevState.template,
                elements: prevState.template.elements.map(el =>
                    el.id === id ? { ...el, locked: !el.locked } : el
                ),
            },
            isDirty: true,
        }));
    };

    _handleZoomChange = (zoom) => {
        this.setState({ zoom });
    };

    // ─── AI Assistant ──────────────────────────────────────────────────────────

    _handleToggleAiPanel = () => {
        if (!canUseAi()) return;
        this.setState(prevState => ({ aiPanelOpen: !prevState.aiPanelOpen }));
    };

    /**
     * Build a compact canonical canvas summary for the API request (Decision 2B).
     * Elements sorted by id for cache-key stability.
     */
    _buildCompactElements = () => {
        const { template } = this.state;
        return template.elements
            .map(el => ({
                id: el.id,
                type: el.type,
                x: el.x,
                y: el.y,
                w: el.w,
                h: el.h,
                zIndex: el.zIndex,
                binding: el.binding || null,
                locked: !!el.locked,
                textPreview: el.freeText ? getPreviewText(el.freeText).slice(0, 40) : null,
            }))
            .sort((a, b) => a.id.localeCompare(b.id));
    };

    /**
     * Apply a validated operation list to a copy of template.elements.
     * Returns { nextElements, affectedIds, lastAddedId }.
     * Defense-in-depth: clamp positions on add/update.
     * Page dims are passed in explicitly so the caller can read them from the
     * same committed state that supplies currentElements (snapshot atomicity).
     */
    _applyOperations = (currentElements, operations, dims) => {
        let nextElements = [...currentElements];
        const affectedIds = [];
        let lastAddedId = null;
        const pageFields = {};

        operations.forEach(op => {
            const opName = (op.op || '').toLowerCase();

            if (opName === 'add' && op.element) {
                // Assign a fresh client id (architecture decision: client assigns on add)
                const newEl = {
                    ...op.element,
                    id: generateId(),
                    zIndex: op.element.zIndex != null
                        ? op.element.zIndex
                        : (nextElements.reduce((acc, e) => Math.max(acc, e.zIndex || 1), 0) + 1),
                    freeText: op.element.freeText !== undefined
                        ? normalizeFreeText(op.element.freeText)
                        : undefined,
                };
                // Clamp position to page bounds (client defense-in-depth)
                const clamped = clampElement(newEl, dims.w, dims.h);
                nextElements = [...nextElements, clamped];
                affectedIds.push(clamped.id);
                lastAddedId = clamped.id;

            } else if (opName === 'update' && op.id) {
                const idx = nextElements.findIndex(el => el.id === op.id);
                if (idx !== -1 && !nextElements[idx].locked) {
                    // Shallow-copy so we never mutate the caller's operations array
                    // (op.fields is a live reference; mutating it would corrupt a re-applied list).
                    const fields = { ...(op.fields || {}) };
                    // Normalize freeText if present
                    if (fields.freeText !== undefined) {
                        fields.freeText = normalizeFreeText(fields.freeText);
                    }
                    const updated = clampElement({ ...nextElements[idx], ...fields }, dims.w, dims.h);
                    nextElements = [
                        ...nextElements.slice(0, idx),
                        updated,
                        ...nextElements.slice(idx + 1),
                    ];
                    affectedIds.push(op.id);
                }

            } else if (opName === 'delete' && op.id) {
                const target = nextElements.find(el => el.id === op.id);
                if (target && !target.locked) {
                    affectedIds.push(op.id);
                    nextElements = nextElements.filter(el => el.id !== op.id);
                }

            } else if (opName === 'updatepage' && op.fields) {
                ['bgColour', 'bgImage', 'bgImageOpacity', 'pageMarginX', 'pageMarginY'].forEach(k => {
                    if (op.fields[k] !== undefined) pageFields[k] = op.fields[k];
                });
            }
        });

        return { nextElements, affectedIds, lastAddedId, pageFields };
    };

    /**
     * Apply an "Applied" operation list to the canvas (shared by the chat path and
     * the base-layout seed path). Reduces ops over the committed template.elements
     * inside a setState updater (snapshot atomicity), sets isDirty, drives the 2s
     * highlight ring, and updates selection.
     *
     * The pre-op snapshot (elements before the ops) is captured INSIDE the updater so it
     * is always consistent with the exact state the reducer saw — no race with any pending
     * setState. It is delivered to the optional onApplied callback once setState commits, so
     * the caller can attach it to a transcript entry for turn-level undo. Transcript handling
     * is left to the caller (it differs between chat and seed).
     */
    _applyAppliedOperations = (operations, onApplied) => {
        let preOpSnapshot = null;

        this.setState(prevState => {
            // Capture the snapshot atomically against the state being reduced.
            // Includes the page-level props so undoing a turn also reverts updatePage ops.
            preOpSnapshot = {
                elements: [...prevState.template.elements],
                page: {
                    bgColour: prevState.template.bgColour,
                    bgImage: prevState.template.bgImage,
                    bgImageOpacity: prevState.template.bgImageOpacity,
                    pageMarginX: prevState.template.pageMarginX,
                    pageMarginY: prevState.template.pageMarginY,
                },
            };
            const dims = getPageDimsMm(prevState.template.pageSize, prevState.template.orientation);

            const { nextElements, affectedIds, lastAddedId, pageFields } =
                this._applyOperations(prevState.template.elements, operations, dims);

            // Selection after apply:
            //   - selected element was deleted → last added (if any) else clear
            //   - else a new element was added → select it
            //   - else keep current selection
            const deletedIds = new Set(
                operations
                    .filter(o => (o.op || '').toLowerCase() === 'delete')
                    .map(o => o.id)
            );
            const currentSelected = prevState.selectedElementId;
            let newSelectedId;
            if (deletedIds.has(currentSelected)) {
                newSelectedId = lastAddedId !== null ? lastAddedId : null;
            } else if (lastAddedId !== null) {
                newSelectedId = lastAddedId;
            } else {
                newSelectedId = currentSelected;
            }

            return {
                template: { ...prevState.template, ...pageFields, elements: nextElements },
                isDirty: true,
                aiHighlightedIds: new Set(affectedIds),
                selectedElementId: newSelectedId,
                selectedElementIds: newSelectedId ? [newSelectedId] : [],
            };
        }, () => {
            // setState committed: the snapshot is consistent with the reduced state.
            if (typeof onApplied === 'function') onApplied(preOpSnapshot);
        });

        // Clear highlight after 2 seconds (cancelled on unmount). Capture this timer's id and
        // only act if it is still the current one and the component is still mounted, so a
        // late callback can never setState on an unmounted component.
        if (this._highlightTimeout) clearTimeout(this._highlightTimeout);
        const timeoutId = setTimeout(() => {
            if (this._highlightTimeout !== timeoutId || !this._isMounted) return;
            this._highlightTimeout = null;
            this.setState({ aiHighlightedIds: new Set() });
        }, 2000);
        this._highlightTimeout = timeoutId;
    };

    /**
     * Send an AI chat turn.
     * - Appends user message immediately
     * - Calls POST api/Voucher/Config/v1/templateAssistant/chat
     * - On Applied: reduce ops, set isDirty, highlight, store snapshot
     * - On error: append safe error bubble, leave canvas unchanged
     */
    /**
     * Translation key and transcript error type for the assistant's error codes (TMPLAI*).
     * Codes without their own message keep the generic "temporarily unavailable" text.
     */
    static AI_ERROR_MESSAGES = {
        TMPLAI0004: { id: 'TemplateAssistant.Error.ModuleMissing', errorType: 'moduleMissing' },
        TMPLAI0005: { id: 'TemplateAssistant.Error.Busy', errorType: 'busy' },
        TMPLAI0006: { id: 'TemplateAssistant.Error.LimitReached', errorType: 'limitReached' },
        TMPLAI0007: { id: 'TemplateAssistant.Error.NotConfigured', errorType: 'notConfigured' },
    };

    _handleAiSend = (instruction) => {
        if (!canUseAi() || !instruction.trim() || this.state.aiPending) return;

        const { intl } = this.props;
        const { template, aiTranscript } = this.state;

        // Build request history from transcript (assistant-level only — exclude errors).
        // Sliding window: send only the last 6 entries (~3 exchanges). The current canvas
        // state is always sent in full separately, so the model stays grounded on the real
        // template; this only caps conversational context to keep token cost/latency bounded.
        const HISTORY_WINDOW = 6;
        const history = aiTranscript
            .filter(e => !e.isError && (e.role === 'user' || e.role === 'assistant'))
            .slice(-HISTORY_WINDOW)
            .map(e => ({ role: e.role, text: e.text }));

        // Append user message to transcript immediately
        const userEntry = { id: generateId(), role: 'user', text: instruction };
        this.setState(prevState => ({
            aiTranscript: [...prevState.aiTranscript, userEntry],
            aiPending: true,
        }));

        const body = {
            instruction,
            page: {
                pageSize: template.pageSize,
                orientation: template.orientation,
            },
            elements: this._buildCompactElements(),
            history,
        };

        postAPI(result => {
            // Bail out if the designer was unmounted while the request was in flight.
            if (!this._isMounted) return;

            const { data, error } = result;

            // Network / auth error
            if (error || !data) {
                const errorText = intl.formatMessage({ id: 'TemplateAssistant.Error.ProviderError' });
                this.setState(prevState => ({
                    aiPending: false,
                    aiTranscript: [
                        ...prevState.aiTranscript,
                        { id: generateId(), role: 'assistant', text: errorText, isError: true, errorType: 'providerError' },
                    ],
                }));
                return;
            }

            // Envelope errors (e.g. validation errors from the server, 400, or the usage limits of US 22580)
            if (data.errors && data.errors.length > 0) {
                const known = VoucherTemplateDesigner.AI_ERROR_MESSAGES[data.errors[0].code];
                const errorText = intl.formatMessage({ id: known ? known.id : 'TemplateAssistant.Error.ProviderError' });
                const errorType = known ? known.errorType : 'providerError';
                this.setState(prevState => ({
                    aiPending: false,
                    aiTranscript: [
                        ...prevState.aiTranscript,
                        { id: generateId(), role: 'assistant', text: errorText, isError: true, errorType },
                    ],
                }));
                return;
            }

            const rs = data.response && data.response[0];
            if (!rs) {
                const errorText = intl.formatMessage({ id: 'TemplateAssistant.Error.ProviderError' });
                this.setState(prevState => ({
                    aiPending: false,
                    aiTranscript: [
                        ...prevState.aiTranscript,
                        { id: generateId(), role: 'assistant', text: errorText, isError: true, errorType: 'providerError' },
                    ],
                }));
                return;
            }

            const status = rs.status || '';

            if (status === 'Applied') {
                const operations = rs.operations || [];

                // Count operation types for the change summary
                const adds    = operations.filter(o => (o.op || '').toLowerCase() === 'add').length;
                const updates = operations.filter(o => (o.op || '').toLowerCase() === 'update').length;
                const deletes = operations.filter(o => (o.op || '').toLowerCase() === 'delete').length;

                // Localize the assistant message key from the server
                const messageKey = rs.assistantMessageKey || 'TemplateAssistant.Reply.Applied';
                const messageText = intl.formatMessage({ id: messageKey });

                // Optional sanitized model prose summary (Applied only) — rendered as plain text
                const modelSummary = rs.assistantMessage || null;

                // Apply ops to the canvas via the shared helper (snapshot atomicity,
                // highlight, selection). The pre-op snapshot is delivered once setState
                // commits, so the transcript entry is attached with a consistent snapshot.
                this._applyAppliedOperations(operations, (preOpSnapshot) => {
                    if (!this._isMounted) return;

                    const assistantEntry = {
                        id: generateId(),
                        role: 'assistant',
                        text: messageText,
                        modelSummary,
                        changesSummary: { adds, updates, deletes },
                        preOpSnapshot,
                    };

                    this.setState(prevState => ({
                        aiPending: false,
                        aiTranscript: [...prevState.aiTranscript, assistantEntry],
                    }));
                });

            } else if (status === 'NothingToApply') {
                const messageKey = rs.assistantMessageKey || 'TemplateAssistant.Reply.NothingToApply';
                const messageText = intl.formatMessage({ id: messageKey });
                this.setState(prevState => ({
                    aiPending: false,
                    aiTranscript: [
                        ...prevState.aiTranscript,
                        { id: generateId(), role: 'assistant', text: messageText },
                    ],
                }));

            } else if (status === 'Rejected') {
                const errorText = intl.formatMessage({ id: 'TemplateAssistant.Error.Rejected' });
                this.setState(prevState => ({
                    aiPending: false,
                    aiTranscript: [
                        ...prevState.aiTranscript,
                        { id: generateId(), role: 'assistant', text: errorText, isError: true, errorType: 'rejected' },
                    ],
                }));

            } else {
                // ProviderError or anything unexpected
                const errorText = intl.formatMessage({ id: 'TemplateAssistant.Error.ProviderError' });
                this.setState(prevState => ({
                    aiPending: false,
                    aiTranscript: [
                        ...prevState.aiTranscript,
                        { id: generateId(), role: 'assistant', text: errorText, isError: true, errorType: 'providerError' },
                    ],
                }));
            }

        }, '/api/Voucher/Config/v1/templateAssistant/chat', body);
    };

    /**
     * Undo the AI operations from a specific transcript turn.
     * Restores template.elements from the stored preOpSnapshot.
     */
    _handleAiUndo = (entryId) => {
        // Resolve the entry by its stable id INSIDE the updater (not a render-time index, and
        // not a value captured outside): this reads the latest state, so a transcript change
        // queued between click and updater can't restore a stale snapshot. Return null to skip
        // the update when the entry is missing/already undone (no needless re-render).
        this.setState(prevState => {
            const entry = prevState.aiTranscript.find(e => e.id === entryId);
            if (!entry || !entry.preOpSnapshot || entry.undone) return null;

            // Snapshot shape: { elements, page } (legacy entries may still be a plain array)
            const snap = entry.preOpSnapshot;
            const snapElements = Array.isArray(snap) ? snap : snap.elements;
            const snapPage = Array.isArray(snap) ? {} : (snap.page || {});

            return {
                template: {
                    ...prevState.template,
                    ...snapPage,
                    elements: snapElements,
                },
                isDirty: true,
                aiTranscript: prevState.aiTranscript.map(e =>
                    e.id === entryId ? { ...e, undone: true } : e
                ),
                // Clear any highlights since we restored the canvas
                aiHighlightedIds: new Set(),
            };
        });
    };

    // ─── New-template base-layout choice ─────────────────────────────────────────

    /**
     * Dismiss the choice modal and keep the empty canvas (current behavior), applying
     * the chosen page format/orientation so a blank template respects the selection.
     */
    _handleStartBlank = ({ pageSize, orientation } = {}) => {
        this.setState(prevState => {
            const nextPageSize = pageSize || prevState.template.pageSize;
            const nextOrientation = orientation || prevState.template.orientation;

            // seed the starter elements only when the canvas is empty, so re-opening
            // the modal never duplicates them
            const dims = getPageDimsMm(nextPageSize, nextOrientation);
            let elements = prevState.template.elements;
            if (!elements || elements.length === 0) {
                elements = createStarterElements(dims.w, dims.h);
            } else {
                elements = elements.map(el => fitElementToPage(el, dims.w, dims.h));
            }

            return {
                newTemplateChoiceModal: false,
                template: {
                    ...prevState.template,
                    pageSize: nextPageSize,
                    orientation: nextOrientation,
                    elements,
                },
            };
        });
    };

    /**
     * "Start with base layout (AI)": call the SAME template-assistant endpoint the
     * chat uses with a curated seed instruction (plus an optional user briefing) over
     * the empty canvas, then apply the returned ops via the shared op-apply helper.
     * On any failure, fall back to a blank canvas and surface a safe localized message
     * — never block the user.
     */
    _handleStartBaseLayout = ({ pageSize, orientation, briefing } = {}) => {
        if (!canUseAi()) {
            this._handleStartBlank({ pageSize, orientation });
            return;
        }

        const { intl } = this.props;

        // Use the chosen page values (fall back to current state if absent).
        const chosenPageSize = pageSize || this.state.template.pageSize;
        const chosenOrientation = orientation || this.state.template.orientation;

        // Close the choice modal, apply the chosen page, and show the pending overlay.
        this.setState(prevState => ({
            newTemplateChoiceModal: false,
            baseLayoutPending: true,
            template: {
                ...prevState.template,
                pageSize: chosenPageSize,
                orientation: chosenOrientation,
            },
        }));

        // Build the instruction: the curated seed plus, if provided, a clearly-delimited
        // objective/briefing section. The briefing is user text that flows to the LLM
        // exactly like a chat instruction; the model may adapt colours/fonts/style to it.
        const trimmedBriefing = (briefing || '').trim();
        const instruction = trimmedBriefing
            ? BASE_LAYOUT_SEED +
              '\n\nOBJECTIVE / BRIEFING (adapt the colours, tone, and visual style — fonts, emphasis, alignment — to this; keep all the bound fields above):\n' +
              trimmedBriefing
            : BASE_LAYOUT_SEED;

        const body = {
            instruction,
            page: {
                pageSize: chosenPageSize,
                orientation: chosenOrientation,
            },
            elements: [],   // from-scratch: empty canvas
            history: [],    // no prior turns
        };

        // On failure the canvas must never stay empty: start from the same starter elements as the
        // blank start, with the page format/orientation the user chose. A known assistant error code
        // (e.g. usage limit reached) explains why instead of the generic message.
        const fallbackToBlank = (errorCode) => {
            const known = errorCode && VoucherTemplateDesigner.AI_ERROR_MESSAGES[errorCode];
            this.setState({ baseLayoutPending: false });
            this._handleStartBlank({ pageSize: chosenPageSize, orientation: chosenOrientation });
            handleNotification({
                warnings: [{ message: intl.formatMessage({ id: known ? known.id : 'TemplateAssistant.BaseLayout.Failed' }) }],
            });
        };

        postAPI(result => {
            // Bail out if the designer was unmounted while the request was in flight.
            if (!this._isMounted) return;

            const { data, error } = result;

            if (error || !data || (data.errors && data.errors.length > 0)) {
                fallbackToBlank(data && data.errors && data.errors.length > 0 ? data.errors[0].code : null);
                return;
            }

            const rs = data.response && data.response[0];
            if (!rs) {
                fallbackToBlank();
                return;
            }

            if (rs.status === 'Applied' && rs.operations && rs.operations.length > 0) {
                // Reuse the shared op-apply machinery (canvas + isDirty + highlight + selection).
                // Clear the pending overlay from the onApplied callback so it is reset only after
                // the apply actually commits (and never left stuck if the apply path changes).
                this._applyAppliedOperations(rs.operations, () => {
                    if (this._isMounted) this.setState({ baseLayoutPending: false });
                });
            } else {
                // NothingToApply / Rejected / ProviderError / empty ops → blank fallback.
                fallbackToBlank();
            }
        }, '/api/Voucher/Config/v1/templateAssistant/chat', body);
    };

    // ─── File export / import ──────────────────────────────────────────────────

    _handleExport = () => {
        const { template } = this.state;
        const payload = {
            name: template.name,
            pageSize: template.pageSize,
            orientation: template.orientation,
            bgColour: template.bgColour,
            bgImage: template.bgImage,
            bgImageOpacity: template.bgImageOpacity ?? 100,
            pageMarginX: template.pageMarginX,
            pageMarginY: template.pageMarginY,
            elements: template.elements,
        };
        const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${template.name || 'template'}.json`;
        a.click();
        URL.revokeObjectURL(url);
    };

    _handleExportHtml = () => {
        const { template, previewLang } = this.state;
        const elements = template.elements || [];
        // eslint-disable-next-line global-require
        const JsBarcode = require('jsbarcode');

        const barcodeDataUrls = {};
        const MM_TO_PX_96DPI = 96 / 25.4;
        elements.filter(el => el.type === 'barcode').forEach(el => {
            const bcVal = el.binding
                ? (SAMPLE_BINDINGS[el.binding] || el.binding)
                : getPreviewText(el.freeText, previewLang) || '123456789';
            const canvas = document.createElement('canvas');
            try {
                JsBarcode(canvas, bcVal || '123456789', {
                    format: 'CODE128',
                    width: 1,
                    height: Math.max(20, Math.round(el.h * MM_TO_PX_96DPI) - 10),
                    fontSize: 10,
                    margin: 0,
                    displayValue: true,
                });
                barcodeDataUrls[el.id] = canvas.toDataURL('image/png');
            } catch (e) {
                barcodeDataUrls[el.id] = null;
            }
        });

        // Reuse the QR canvases rendered on the designer canvas so the export
        // carries real QR images instead of placeholder boxes
        elements.filter(el => el.type === 'qr').forEach(el => {
            const qrCanvas = document.querySelector(`[data-qr-id="${el.id}"] canvas`);
            if (qrCanvas) {
                try {
                    barcodeDataUrls[el.id] = qrCanvas.toDataURL('image/png');
                } catch (e) {
                    barcodeDataUrls[el.id] = null;
                }
            }
        });

        const html = buildPrintHtml(template, elements, barcodeDataUrls, previewLang);
        const blob = new Blob([html], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${template.name || 'template'}.html`;
        a.click();
        URL.revokeObjectURL(url);
    };

    _handleImportClick = () => {
        if (this._fileInputRef.current) this._fileInputRef.current.click();
    };

    _handleImportFile = (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;
        // Reset so the same file can be re-imported
        e.target.value = '';

        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                this._loadLayout(JSON.parse(evt.target.result));
            } catch {
                handleNotification({ errors: [{ message: 'Invalid template file.' }] });
            }
        };
        reader.readAsText(file);
    };

    // Layout in the Export JSON format onto the canvas, keeping its page format and orientation
    // (Import JSON and inspiration templates)
    _loadLayout = (parsed, extraState = {}) => {
        const elements = (parsed.elements || []).map(el =>
            el.freeText !== undefined ? { ...el, freeText: normalizeFreeText(el.freeText) } : el
        );
        this.setState(prevState => ({
            template: {
                ...prevState.template,
                name: parsed.name || prevState.template.name,
                pageSize: parsed.pageSize || 'A4',
                orientation: parsed.orientation || 'portrait',
                bgColour: parsed.bgColour || '#ffffff',
                bgImage: parsed.bgImage || '',
                bgImageOpacity: parsed.bgImageOpacity ?? 100,
                pageMarginX: parsed.pageMarginX || 0,
                pageMarginY: parsed.pageMarginY || 0,
                elements,
            },
            selectedElementId: null,
            selectedElementIds: [],
            isDirty: true,
            ...extraState,
        }));
    };

    // "Start from an inspiration template": a copy of the base design's elements as a new, unsaved
    // template; the group's design is only created when the user saves (the base id is not kept)
    _handleStartInspiration = (base) => {
        const { id, ...layout } = JSON.parse(JSON.stringify(base));
        this._loadLayout(layout, { newTemplateChoiceModal: false });
    };

    // ─── Navigation guard ──────────────────────────────────────────────────────

    _handleBack = () => {
        if (this.state.isDirty) {
            this.setState({ unsavedChangesModal: true });
        } else {
            this.props.history.push('/VoucherTemplates');
        }
    };

    _confirmLeave = () => {
        this.setState({ unsavedChangesModal: false });
        this.props.history.push('/VoucherTemplates');
    };

    _cancelLeave = () => {
        this.setState({ unsavedChangesModal: false });
    };

    // ─── Render ────────────────────────────────────────────────────────────────

    render() {
        const {
            block,
            error,
            template,
            selectedElementId,
            zoom,
            previewLang,
            isDirty,
            previewModal,
            unsavedChangesModal,
            aiPanelOpen,
            aiTranscript,
            aiPending,
            aiHighlightedIds,
            newTemplateChoiceModal,
            baseLayoutPending,
        } = this.state;

        const selectedElement = template.elements.find(el => el.id === selectedElementId) || null;
        const aiEnabled = canUseAi();

        return (
            <StyledCard
                icon="fa-regular fa-pen-to-square"
                title="VoucherTemplateDesigner.title"
                id="voucherTemplateDesigner"
                error={error}
                block={block}
            >
                {/* SPA navigation guard — intercepts React Router route changes when there are unsaved changes */}
                <Prompt
                    when={isDirty}
                    message={() => ''}
                />

                {/* Main designer area */}
                <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 160px)', border: '1px solid #dee2e6', borderRadius: 4, overflow: 'hidden' }}>
                    {/* Hidden file input for template import */}
                    <input
                        ref={this._fileInputRef}
                        type="file"
                        accept=".json,application/json"
                        style={{ display: 'none' }}
                        onChange={this._handleImportFile}
                    />

                    {/* Toolbar */}
                    <DesignerToolbar
                        templateName={template.name}
                        onNameChange={this._handleNameChange}
                        onBack={this._handleBack}
                        onPreview={this._handlePreview}
                        onSave={this._handleSave}
                        onSaveClose={this._handleSaveClose}
                        onExportJson={this._handleExport}
                        onExportHtml={this._handleExportHtml}
                        onImportClick={this._handleImportClick}
                        aiPanelOpen={aiPanelOpen}
                        aiPending={aiPending}
                        aiEnabled={aiEnabled}
                        onToggleAi={this._handleToggleAiPanel}
                    />

                    {/* Three-panel layout */}
                    <div style={{ display: 'flex', flexGrow: 1, overflow: 'hidden', minHeight: 0 }}>
                        {/* Left: Element palette */}
                        <ElementPalette
                            elements={template.elements}
                            selectedElementId={selectedElementId}
                            selectedElementIds={this.state.selectedElementIds}
                            onAddElement={this._handleAddElementByType}
                            onSelectElement={this._handleSelectElement}
                            onToggleLock={this._handleToggleLockElement}
                            onDuplicateElement={this._handleDuplicateElement}
                            onDeleteElement={this._handleDeleteElement}
                            onMoveLayer={this._handleMoveLayer}
                            onReorderLayer={this._handleReorderLayer}
                        />

                        {/* Centre: Canvas */}
                        <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minWidth: 0, overflow: 'hidden' }}>
                            {/* Language selector bar */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '4px 12px', borderBottom: '1px solid #dee2e6', backgroundColor: '#f8f9fa', flexShrink: 0 }}>
                                <span style={{ fontSize: '0.75rem', color: '#6c757d', marginRight: 4 }}>
                                    <FormattedMessage id="TemplateDesigner.PreviewLanguage" />:
                                </span>
                                {getOrderedTemplateLanguages().map(({ code, flagClass, label }) => (
                                    <button
                                        key={code}
                                        type="button"
                                        title={label}
                                        onClick={() => this.setState({ previewLang: code })}
                                        style={{
                                            padding: '3px 6px',
                                            border: '1px solid #ced4da',
                                            borderRadius: 3,
                                            backgroundColor: previewLang === code ? '#e8f0fe' : '#fff',
                                            outline: previewLang === code ? '2px solid #007bff' : 'none',
                                            cursor: 'pointer',
                                            opacity: previewLang === code ? 1 : 0.5,
                                            lineHeight: 1,
                                        }}
                                    >
                                        <span className={flagClass} style={{ fontSize: '1.1rem' }} />
                                    </button>
                                ))}
                            </div>
                            <DesignerCanvas
                                template={template}
                                zoom={zoom}
                                previewLang={previewLang}
                                elements={template.elements}
                                bgColour={template.bgColour}
                                bgImage={template.bgImage}
                                bgImageOpacity={template.bgImageOpacity}
                                pageMarginX={template.pageMarginX}
                                pageMarginY={template.pageMarginY}
                                selectedElementId={selectedElementId}
                                selectedElementIds={this.state.selectedElementIds}
                                onSelectElement={this._handleSelectElement}
                                onMoveElement={this._handleMoveElement}
                                onResizeElement={this._handleResizeElement}
                                onRotateElement={this._handleRotateElement}
                                onRadiusElement={this._handleRadiusElement}
                                onDuplicateElement={this._handleDuplicateElement}
                                onAddElement={this._handleAddElementObject}
                                onZoomChange={this._handleZoomChange}
                                aiHighlightedIds={aiHighlightedIds}
                            />
                        </div>

                        {/* Right: Property inspector */}
                        <div style={{ width: 320, flexShrink: 0, borderLeft: '1px solid #dee2e6', overflowY: 'auto' }}>
                            <PropertyInspector
                                element={selectedElement}
                                template={template}
                                onChange={this._handleChangeElement}
                                onVisibilityChange={this._handleVisibilityChange}
                                onDelete={this._handleDeleteElement}
                                onDuplicate={this._handleDuplicateElement}
                                onAlign={this._handleAlign}
                                multiSelection={this.state.selectedElementIds.length > 1}
                                onPageSizeChange={this._handlePageSizeChange}
                                onOrientationChange={this._handleOrientationChange}
                                onBgColourChange={this._handleBgColourChange}
                                onBgImageChange={this._handleBgImageChange}
                                onBgImageOpacityChange={this._handleBgImageOpacityChange}
                                onPageMarginXChange={this._handlePageMarginXChange}
                                onPageMarginYChange={this._handlePageMarginYChange}
                            />
                        </div>

                        {/* 4th column on desktop (>=1024px); bottom-sheet overlay on tablet
                            (768-1023px) via the .ai-panel-column media query in custom.css.
                            The .open modifier scopes the tablet overrides to the open state. */}
                        {aiEnabled && <div
                            className={`ai-panel-column${aiPanelOpen ? ' open' : ''}`}
                            style={{
                                width: aiPanelOpen ? 360 : 0,
                                flexShrink: 0,
                                borderLeft: aiPanelOpen ? '1px solid #dee2e6' : 'none',
                                overflow: 'hidden',
                                transition: 'width 0.2s ease',
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            {aiPanelOpen && (
                                <AiAssistantPanel
                                    transcript={aiTranscript}
                                    isPending={aiPending}
                                    onSend={this._handleAiSend}
                                    onUndo={this._handleAiUndo}
                                    onClose={this._handleToggleAiPanel}
                                />
                            )}
                        </div>}
                    </div>
                </div>

                {/* Preview modal */}
                {previewModal && (
                    <TemplateDesignerPreviewModal
                        isOpen={previewModal}
                        toggle={this._togglePreviewModal}
                        template={template}
                    />
                )}

                {/* Unsaved changes guard modal */}
                {unsavedChangesModal && (
                    <ConfirmActionModal
                        modal={unsavedChangesModal}
                        toggleModal={this._cancelLeave}
                        actionFunction={this._confirmLeave}
                        text={<FormattedMessage id="TemplateDesigner.UnsavedChanges" />}
                        block={false}
                    />
                )}

                {/* New-template base-layout choice (from-scratch only) */}
                {newTemplateChoiceModal && (
                    <NewTemplateChoiceModal
                        isOpen={newTemplateChoiceModal}
                        aiEnabled={aiEnabled}
                        onBase={this._handleStartBaseLayout}
                        onBlank={this._handleStartBlank}
                        baseTemplates={this.state.baseTemplates}
                        onInspiration={this._handleStartInspiration}
                    />
                )}

                {/* Base-layout generation overlay (AI seed in flight) */}
                {baseLayoutPending && (
                    <div
                        role="status"
                        aria-live="polite"
                        style={{
                            position: 'fixed',
                            top: 0, left: 0, right: 0, bottom: 0,
                            backgroundColor: 'rgba(255,255,255,0.7)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 1060,
                        }}
                    >
                        <i className="fa-solid fa-robot fa-bounce" aria-hidden="true" style={{ fontSize: '2rem', color: '#6f42c1', marginBottom: 12 }} />
                        <div style={{ fontSize: '0.95rem', color: '#495057' }}>
                            <i className="fa-solid fa-circle-notch fa-spin mr-2" aria-hidden="true" />
                            <FormattedMessage id="TemplateAssistant.BaseLayout.Generating" />
                        </div>
                    </div>
                )}
            </StyledCard>
        );
    }
}

export default injectIntl(VoucherTemplateDesigner);
